import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { USER_URL } from '../../../constant/api';
import { IoCloseSharp } from 'react-icons/io5';

function AddToCartModel({ selectedProduct, isModalOpen, closeModal }) {
    const [quantity, setQuantity] = useState(0);
    const [enteredQuantity, setEnteredQuantity] = useState(0);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        if (isModalOpen && selectedProduct) {
            setQuantity(selectedProduct.min_order_quantity || 1);
            setEnteredQuantity(0);
        }
    }, [isModalOpen, selectedProduct]);

    const handleSelectMinimumQuantity = (qty) => {
        setQuantity(qty);
        setEnteredQuantity(0);
    };

    const addToCart = async (product, totalQuantity) => {
        // Get product ID - handle both formats (id from ProductCard, product_id from WishlistCard)
        const productId = product?.id || product?.product_id;
        
        if (!product || !productId) {
            console.error('Invalid product or missing product ID');
            return;
        }

        if (totalQuantity <= 0) {
            alert('Please enter a valid quantity.');
            return;
        }

        setLoading(true);
        try {
            await USER_URL.post(`/orders/cart/`, {
                product_id: productId, // Use the resolved product ID
                quantity: totalQuantity
            });

            navigate('/cart');
        } catch (error) {
            console.error('Error adding product to cart:', error);
        } finally {
            setLoading(false);
        }
    };

    const totalQuantity = enteredQuantity > 0 ? enteredQuantity : quantity;

    return (
        <div>
            {isModalOpen && selectedProduct && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                    <div className="bg-white p-6 rounded-lg max-w-md w-auto md:w-full shadow-lg">
                        <div className="flex justify-between items-center">
                            <h2 className="text-xl font-semibold mb-4">
                                {selectedProduct.name || selectedProduct.product_name}
                            </h2>
                            <span className="text-2xl cursor-pointer" onClick={closeModal}>
                                <IoCloseSharp />
                            </span>
                        </div>

                        <p className="line-clamp-2">{selectedProduct.description}</p>
                        <p className="text-lg font-bold mt-2">
                            Price: {selectedProduct.currency_symbol || '₹'}{selectedProduct.price || selectedProduct.price_per_item}
                        </p>

                        {/* Minimum Quantity Selector */}
                        <label className="block my-4">
                            <span className="text-gray-700">Select Minimum Quantity</span>
                            <div className="flex flex-row justify-between mt-2 flex-wrap gap-2">
                                {[
                                    selectedProduct.min_order_quantity,
                                    selectedProduct.min_order_quantity_two,
                                    selectedProduct.min_order_quantity_three,
                                    selectedProduct.min_order_quantity_four,
                                    selectedProduct.min_order_quantity_five
                                ]
                                    .filter(Boolean)
                                    .map((qty) => (
                                        <div
                                            key={qty}
                                            onClick={() => handleSelectMinimumQuantity(qty)}
                                            className={`cursor-pointer px-4 py-2 border rounded-lg text-center ${
                                                quantity === qty && enteredQuantity === 0
                                                    ? 'bg-brand-primary text-white'
                                                    : 'bg-white hover:border-brand-primary hover:text-brand-primary'
                                            }`}
                                        >
                                            {qty}
                                        </div>
                                    ))}
                            </div>
                        </label>

                        {/* Manual Quantity Input */}
                        <label className="block my-4">
                            <span className="text-gray-700">Or Enter Custom Quantity</span>
                            <input
                                type="number"
                                value={enteredQuantity === 0 ? '' : enteredQuantity}
                                onChange={(e) => {
                                    const value = Number(e.target.value);
                                    setEnteredQuantity(isNaN(value) || value < 0 ? 0 : value);
                                }}
                                className="w-full p-2 border rounded mt-2"
                                placeholder="Enter quantity"
                                min="0"
                            />
                        </label>

                        <div className="mt-2 text-lg font-semibold">Total Quantity: {totalQuantity}</div>
                        <div className="mt-2 text-lg font-semibold">
                            Total Price: {selectedProduct.currency_symbol || '₹'}{((selectedProduct.price || selectedProduct.price_per_item) * totalQuantity || 0).toFixed(2)}
                        </div>

                        {/* Action Button */}
                        <div className="flex justify-between space-x-4 mt-4">
                            <button
                                onClick={() => addToCart(selectedProduct, totalQuantity)}
                                className="hover:bg-brand-primary text-brand-primary px-4 py-2 rounded w-full border-brand-primary border-solid border-2 hover:text-white ease-in duration-200"
                                disabled={loading || totalQuantity <= 0}
                            >
                                {loading ? 'Adding...' : 'Add to Cart'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default AddToCartModel;
